import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './ImageShapes.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import { getButtonEffectStyles } from '../utils/buttonEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { imageShapes } from '../WidgetEditComponents/ImageShapeSelector';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import HighlightText from '../WidgetEditComponents/HighlightText';
import AIRefreshIcon from '../../components/AIRefreshIcon';


// appendPx wird jetzt aus LayoutUtils importiert

function appendPercent(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    const percentValue = Math.min(Number(value), 100);
    return `${percentValue}%`;
}

function HiWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const defaultProfileImage = '/img/blogger.webp';
    const deleteLogo = '/img/delete.svg';

    const fonts = useFont();
    
    // INLINE-EDITING SYSTEM (wie HeroHeader)
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    const [isUploadingProfile, setIsUploadingProfile] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // ✅ NEW LAYOUT SYSTEM 2025 - RESPONSIVE LAYOUT DETECTION
    const [isMobileLayout, setIsMobileLayout] = useState(null); // ✅ NULL initial state verhindert Flackern
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // Layout Configuration
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'classic-profile';
        
        const layouts = {
            'classic-profile': {
                direction: 'row',
                imageFlex: '0 0 auto',
                textFlex: '1',
                imageAlign: 'center',
                textAlign: 'left',
                imageSize: 'normal',
                spacing: 'normal',
                mobileStack: true
            },
            'image-focus': {
                direction: 'row',
                imageFlex: '0 0 auto',
                textFlex: '1',
                imageAlign: 'center',
                textAlign: 'left',
                imageSize: 'large',
                spacing: 'wide',
                mobileStack: true
            },
            'centered-profile': {
                direction: 'column',
                imageFlex: '0 0 auto',
                textFlex: '0 0 auto',
                imageAlign: 'center',
                textAlign: 'center',
                imageSize: 'normal',
                spacing: 'normal',
                mobileStack: false
            },
            'reverse-profile': {
                direction: 'row-reverse',
                imageFlex: '0 0 auto',
                textFlex: '1',
                imageAlign: 'center',
                textAlign: 'right',
                imageSize: 'normal',
                spacing: 'normal',
                mobileStack: true
            },
            'text-only-center': {
                direction: 'column',
                imageFlex: '0 0 auto',
                textFlex: '1',
                imageAlign: 'center',
                textAlign: 'center',
                imageSize: 'hidden',
                spacing: 'normal',
                mobileStack: false,
                showImage: false
            },
            'full-height-left': {
                direction: 'row',
                imageFlex: '0 0 auto', // ✅ BLEIBT: Feste Breite, aber Container muss Height erlauben
                textFlex: '1',
                imageAlign: 'stretch', // ✅ STRETCH für Full-Height
                textAlign: 'left',
                imageSize: 'fullHeight',
                spacing: 'normal',
                mobileStack: true,
                showImage: true,
                imageMode: 'fullHeight'
            },
            'full-height-right': {
                direction: 'row-reverse',
                imageFlex: '0 0 auto', // ✅ BLEIBT: Feste Breite, aber Container muss Height erlauben
                textFlex: '1',
                imageAlign: 'stretch', // ✅ STRETCH für Full-Height
                textAlign: 'right',
                imageSize: 'fullHeight',
                spacing: 'normal',
                mobileStack: true,
                showImage: true,
                imageMode: 'fullHeight'
            },
        };
        
        return layouts[layoutType] || layouts['classic-profile'];
    };

    const layoutConfig = getLayoutConfig();


    // Get image size based on layout
    const getImageSize = () => {
        // ✅ RESPONSIVE IMAGE SIZE - Mobile/Desktop Support
        const baseSize = getResponsiveValue(data, 'profileImageSize', isMobileLayout, 200);
        switch(layoutConfig.imageSize) {
            case 'small': return Math.max(baseSize * 0.6, 80);
            case 'large': return Math.min(baseSize * 1.4, 300);
            case 'fullHeight': return '100%';
            case 'fullWidth': return '100%';
            case 'hidden': return 0;
            default: return baseSize;
        }
    };

    // Get image styles for full-height/width modes
    const getImageStyles = () => {
        const baseStyles = {
            objectFit: 'cover',
            border: data.profileImageBorder ? `${data.profileImageBorderWidth || 4}px solid ${data.profileImageBorderColor || '#FFFFFF'}` : 'none',
            boxShadow: data.profileImageShadow ? '0 8px 24px rgba(0, 0, 0, 0.15)' : 'none'
        };

        if (layoutConfig.imageMode === 'fullHeight') {
            return {
                ...baseStyles,
                // ✅ FULL-HEIGHT FIX: Image füllt kompletten Container
                width: (isMobileLayout || false) ? 
                    appendPx(getResponsiveValue(data, 'profileImageSize', isMobileLayout, 200)) : 
                    '100%', // ✅ Desktop: Volle Container-Breite
                height: (isMobileLayout || false) ? 'auto' : '100%', // ✅ Desktop: Volle Container-Höhe
                minHeight: (isMobileLayout || false) ? 'auto' : '100%',
                borderRadius: '0', // ✅ Keine Rundungen für Full-Height
                // ✅ KEINE maxWidth/maxHeight Begrenzungen für Full-Height
                maxWidth: (isMobileLayout || false) ? appendPx(getResponsiveValue(data, 'profileImageSize', isMobileLayout, 200)) : 'none',
                maxHeight: (isMobileLayout || false) ? appendPx(getResponsiveValue(data, 'profileImageSize', isMobileLayout, 200)) : 'none',
                // ✅ OBJECT-FIT für bessere Image-Darstellung
                objectFit: 'cover',
                objectPosition: 'center'
            };
        }

        if (layoutConfig.imageMode === 'fullWidth') {
            return {
                ...baseStyles,
                width: '100%',
                height: appendPx(Math.min(getResponsiveValue(data, 'profileImageSize', isMobileLayout, 200), 290)),
                borderRadius: '0',
                marginBottom: '20px',
                maxHeight: '290px'
            };
        }

        return {
            ...baseStyles,
            width: appendPx(Math.min(getImageSize(), 290)),
            height: appendPx(Math.min(getImageSize(), 290)),
            maxWidth: '290px',
            maxHeight: '290px'
        };
    };

    // Get spacing based on layout
    const getSpacing = () => {
        const baseSpacing = data.desktopSpacing || 40;
        switch(layoutConfig.spacing) {
            case 'tight': return Math.max(baseSpacing * 0.5, 15);
            case 'compact': return Math.max(baseSpacing * 0.7, 20);
            case 'wide': return Math.min(baseSpacing * 1.5, 80);
            default: return baseSpacing;
        }
    };

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }


    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none'
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FULL-HEIGHT LAYOUTS: Brauchen feste Höhe für 100% Image-Height
        ...(layoutConfig.imageMode === 'fullHeight' && !isMobileLayout && {
            height: appendPx(data.height || 600), // Feste Höhe für Full-Height auf Desktop
            minHeight: appendPx(data.height || 600)
        }),
        // ✅ FALLBACK für alte Height-Property
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile: Template-Default
                (data.height || 650)         // Desktop: Template-Default
            )
        })
    };

    // Dynamische Breakpoint-Erkennung basierend auf Container-Breite
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        // Initial check
        checkContainerWidth();

        // Listen for window resize
        window.addEventListener('resize', checkContainerWidth);
        
        // Listen for container size changes (für Panel-Toggles)
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Smart Outside-Click Handler - nur für echte Außenklicks
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            // Smarte Erkennung: Ist es ein echter Außenklick?
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            // Definiere was ein "echter Außenklick" ist
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                // Speichern und beenden
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                // Edit-Modus beenden
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    const handleButtonClick = () => {
        if (!isEditMode && data.buttonLink) {
            window.open(data.buttonLink, '_blank');
        }
    };

    // INLINE-EDITING FUNCTIONS (kopiert von HeroHeader)
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        // Toggle functionality: If already editing this field, cancel editing
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        // Update the widget data
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        // Reset inline editing state
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        // EINFACHE Keyboard-Logik
        const isTextareaField = fieldName.includes('description') || fieldName.includes('additional');
        
        if (e.key === 'Enter') {
            if (isTextareaField) {
                // Descriptions/Additional: Enter = neue Zeile, Ctrl+Enter = speichern
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
                // Enter allein = neue Zeile (normales Verhalten)
            } else {
                // Alle anderen: Enter = speichern
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Image Upload Function
    const handleImageUpload = (fieldName, setUploadingState) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file && onUpdate) {
                setUploadingState(true);
                
                // Simuliere Upload (in echter App: Firebase Upload)
                setTimeout(() => {
                    const fakeUrl = URL.createObjectURL(file);
                    const updatedData = { ...data, [fieldName]: fakeUrl };
                    onUpdate(updatedData);
                    setUploadingState(false);
                }, 2000);
            }
        };
        input.click();
    };

    // RENDER EDITABLE TEXT FUNCTION (kopiert von HeroHeader)
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            // In Live-Modus: Button-Click-Funktionalität beibehalten
            if (Element === 'button' && currentValue) {
                return (
                    <Element 
                        style={style}
                        onClick={handleButtonClick}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                        }}
                    >
                        {currentValue}
                    </Element>
                );
            }
            
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: 'pre-wrap' // Behält Zeilenumbrüche bei
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            // Verwende die gespeicherten Original-Layout-Eigenschaften
            const layoutData = originalLayout;
            
            // EINFACHE Detection - alle Text-Elemente als Textarea, Buttons als Input
            const isTextarea = Element !== 'button';
            
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: Element === 'button' ? '-11px' : '-11px',
                    right: Element === 'button' ? '-6px' : '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    {/* Grüner Haken - Speichern */}
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(16, 185, 129, 0.3)'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    {/* Rotes X - Abbrechen */}
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 3px rgba(239, 68, 68, 0.3)'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );

            return isTextarea ? (
                <div style={{ 
                    position: 'relative',
                    width: '100%',
                    marginTop: layoutData.marginTop || '0',
                    marginBottom: layoutData.marginBottom || '0',
                    marginLeft: layoutData.marginLeft || '0',
                    marginRight: layoutData.marginRight || '0'
                }}>
                    <textarea
                        value={inlineValue}
                        onChange={(e) => {
                            setInlineValue(e.target.value);
                            const target = e.target;
                            // ✅ Cursor-Position merken BEVOR wir die Höhe ändern
                            const cursorPosition = target.selectionStart;
                            setTimeout(() => {
                                target.style.height = 'auto';
                                target.style.height = target.scrollHeight + 'px';
                                // ✅ Cursor-Position wiederherstellen
                                target.setSelectionRange(cursorPosition, cursorPosition);
                            }, 0);
                        }}
                        onKeyDown={(e) => {
                            handleInlineKeyPress(e, fieldName);
                            if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                setTimeout(() => {
                                    const target = e.target;
                                    target.style.height = 'auto';
                                    target.style.height = target.scrollHeight + 'px';
                                }, 0);
                            }
                        }}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        ref={(textarea) => {
                            if (textarea) {
                                setTimeout(() => {
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                    // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                    if (textarea.selectionStart === 0 && textarea.selectionEnd === 0) {
                                        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                                    }
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            background: 'transparent',
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            padding: '4px 8px',
                            margin: '0',
                            width: '100%',
                            minHeight: layoutData.height || 'auto',
                            // GRADIENT-FIX: Grauer Text während Bearbeitung
                            color: '#333333',
                            backgroundImage: 'none',
                            backgroundClip: 'unset',
                            webkitBackgroundClip: 'unset',
                            webkitTextFillColor: '#333333',
                            // Original-Text-Eigenschaften
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            lineHeight: style.lineHeight,
                            letterSpacing: style.letterSpacing,
                            textAlign: style.textAlign || 'inherit',
                            whiteSpace: 'pre-wrap'
                        }}
                    />
                    {renderActionButtons()}
                </div>
            ) : (
                // BUTTONS: Input-Felder mit exakter Größen-Erhaltung
                <input
                    type="text"
                    value={inlineValue}
                    onChange={(e) => {
                        setInlineValue(e.target.value);
                        // ✅ Cursor-Position wird automatisch beibehalten bei normalen Inputs
                    }}
                    onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                    onBlur={() => handleInlineSubmit(fieldName)}
                    placeholder={placeholder}
                    autoFocus
                    ref={(input) => {
                        if (input) {
                            setTimeout(() => {
                                // ✅ NUR beim ersten Laden ans Ende setzen, nicht bei jedem Re-Render
                                if (input.selectionStart === 0 && input.selectionEnd === 0) {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }
                            }, 0);
                        }
                    }}
                    style={{
                        ...style,
                        // EXAKTE Button-Dimensionen beibehalten
                        width: layoutData.width || style.width,
                        height: layoutData.height || style.height,
                        minWidth: layoutData.width || style.minWidth,
                        maxWidth: layoutData.width || 'none',
                        minHeight: layoutData.height || 'auto',
                        maxHeight: layoutData.height || 'none',
                        marginTop: layoutData.marginTop || '0',
                        marginBottom: layoutData.marginBottom || '0',
                        marginLeft: layoutData.marginLeft || '0',
                        marginRight: layoutData.marginRight || '0',
                        // Button-Eigenschaften beibehalten
                        background: style.background || style.backgroundColor,
                        border: style.border || 'none',
                        borderRadius: style.borderRadius,
                        padding: style.padding,
                        display: style.display,
                        alignItems: style.alignItems,
                        justifyContent: style.justifyContent,
                        alignSelf: style.alignSelf,
                        boxShadow: 'none',
                        cursor: 'text',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                />
            );
        }

        // Edit-Modus: Inline-Editing ermöglichen
        const handleElementClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // LEAN Layout-Erfassung - nur das Nötige
            const rect = e.target.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(e.target);
            
            const layoutData = {
                width: rect.width + 'px',
                height: rect.height + 'px',
                marginTop: computedStyle.marginTop,
                marginBottom: computedStyle.marginBottom,
                marginLeft: computedStyle.marginLeft,
                marginRight: computedStyle.marginRight
            };
            
            setOriginalLayout(layoutData);
            handleInlineEdit(fieldName, currentValue);
        };

        return (
            <Element 
                style={{
                    ...style,
                    cursor: Element === 'button' ? 'pointer' : 'text',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    padding: Element === 'button' ? style.padding : '4px 8px',
                    borderRadius: style.borderRadius || (Element === 'button' ? style.borderRadius : '4px'),
                    border: Element === 'button' ? style.border : 'none',
                    boxShadow: Element === 'button' ? style.boxShadow : 'none'
                }}
                onClick={handleElementClick}
                onMouseEnter={(e) => {
                    // EINFACH: Nur Cursor ändern, keine visuellen Effekte
                    if (Element !== 'button') {
                        e.target.style.cursor = 'text';
                    } else {
                        e.target.style.cursor = 'pointer';
                    }
                }}
                onMouseLeave={(e) => {
                    e.target.style.cursor = Element === 'button' ? 'pointer' : 'text';
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
                {/* Blinkender Cursor beim Editieren */}
                {isEditMode && isInlineEditing === fieldName && (
                    <span style={{
                        display: 'inline',
                        animation: 'blink 1s infinite',
                        fontSize: 'inherit',
                        color: 'inherit',
                        marginLeft: '1px'
                    }}>
                        |
                    </span>
                )}
            </Element>
        );
    };

    // Get the CSS class for the selected image shape
    const getImageShapeClass = () => {
        const shapeId = data.profileImageShape || 'circle';
        const shape = imageShapes.find(s => s.id === shapeId);
        return shape ? shape.className : 'image-shape-circle';
    };

    // Get padding and width for full-height layouts
    const getContainerStyles = () => {
        const baseStyles = {
            ...widgetStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: layoutConfig.textAlign
        };

        // Full-Height Layouts: Kein Padding, volle Breite
        if (layoutConfig.imageMode === 'fullHeight') {
            return {
                ...baseStyles,
                padding: '0',
                width: '100%'
            };
        }

        // Standard Layouts: Verwende Revolutionary Layout System Padding
        // Keine hardcoded Padding-Werte mehr - das übernimmt generateLayoutStyles
        return baseStyles;
    };

    return (
        <>
            {/* CSS Animations - Enhanced with Button Animations */}
            <style>
                {`
                    @keyframes blink {
                        0%, 50% { opacity: 1; }
                        51%, 100% { opacity: 0; }
                    }
                    
                    @keyframes heroImageUploadSpin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    /* 🎬 REVOLUTIONARY BUTTON ANIMATIONS */
                    @keyframes gradientShift {
                        0% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }
                    
                    @keyframes gentle-pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.02); opacity: 0.9; }
                    }

                    @keyframes gentle-sway {
                        0%, 100% { transform: translateX(0px); }
                        25% { transform: translateX(-2px); }
                        75% { transform: translateX(2px); }
                    }

                    @keyframes subtle-float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-3px); }
                    }

                    @keyframes gentle-breathe {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.03); opacity: 0.85; }
                    }

                    @keyframes gentle-wobble {
                        0%, 100% { transform: rotate(0deg); }
                        25% { transform: rotate(-1deg); }
                        75% { transform: rotate(1deg); }
                    }
                `}
            </style>
            <div 
                className={`widget-container hi-widget layout-${data.layoutType || 'classic-profile'}`}
                style={getContainerStyles()} 
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
            >
            {/* Delete Button */}
            {isEditMode && (
                <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'HiWidget'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [HIWIDGET] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={(event) => handleDelete(event, data.id)}
                />
            )}
            
            {/* Main Content Container - Vereinfacht für Full-Height */}
            <div style={{
                // ✅ SIMPLIFIED LAYOUT: Einfaches Flex-Layout für Full-Height
                ...(layoutConfig.imageMode === 'fullHeight' && !isMobileLayout ? {
                    // FULL-HEIGHT: Vereinfachtes Layout
                    display: 'flex',
                    flexDirection: data.desktopFlipSides ? 
                        (layoutConfig.direction === 'row' ? 'row-reverse' : 'row') : 
                        layoutConfig.direction,
                    height: '100%',
                    width: '100%',
                    alignItems: 'stretch', // ✅ Stretch für volle Höhe
                    gap: '0px', // Kein Gap bei Full-Height
                    padding: '0', // ✅ KEIN PADDING für Full-Height
                    overflow: 'hidden'
                } : {
                    // STANDARD LAYOUTS: Normale Flex-Logic
                    display: 'flex',
                    flexDirection: (() => {
                        if (isMobileLayout === null) return layoutConfig.direction;
                        if (isMobileLayout && layoutConfig.mobileStack) {
                            const stackOrder = data.mobileStackOrder || 'image-first';
                            const shouldFlip = data.mobileFlipSides;
                            if (stackOrder === 'text-first' || shouldFlip) {
                                return 'column-reverse';
                            }
                            return 'column';
                        } else {
                            const baseDirection = layoutConfig.direction;
                            const shouldFlip = data.desktopFlipSides;
                            if (shouldFlip) {
                                if (baseDirection === 'row') return 'row-reverse';
                                if (baseDirection === 'row-reverse') return 'row';
                            }
                            return baseDirection;
                        }
                    })(),
                    alignItems: (() => {
                        if (isMobileLayout === null) return 'center';
                        if (isMobileLayout || layoutConfig.direction === 'column') {
                            return 'center';
                        }
                        return data.textVerticalAlign || layoutConfig.imageAlign || 'center';
                    })(),
                    gap: getResponsiveValue(data, 'imageTextGap', isMobileLayout || false, (isMobileLayout || false) ? 20 : 40) + 'px',
                    maxWidth: `${data.maxWidth || 1200}px`,
                    width: '100%',
                    justifyContent: layoutConfig.direction === 'column' ? 'center' : 'flex-start',
                    padding: '0'
                })
            }}>
                {/* Profile Image - Simplified für Full-Height */}
                {layoutConfig.showImage !== false && (
                    <div style={{
                        // ✅ SIMPLIFIED FULL-HEIGHT: Einfaches Layout
                        ...(layoutConfig.imageMode === 'fullHeight' && !isMobileLayout ? {
                            // FULL-HEIGHT: Prozent-basierte Image-Container Breite
                            width: getResponsiveValue(data, 'fullHeightImageWidth', isMobileLayout, 35) + '%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0', // ✅ KEIN PADDING - Image klebt am Rand
                            overflow: 'hidden'
                        } : {
                            // STANDARD LAYOUTS: Normale Flex-Logic
                            flex: layoutConfig.imageFlex,
                            display: 'flex',
                            justifyContent: layoutConfig.direction === 'column' ? 'center' : (layoutConfig.direction === 'row-reverse' ? 'flex-end' : 'flex-start'),
                            alignItems: 'center',
                            height: 'auto',
                            padding: (() => {
                                const contentSpacing = (isMobileLayout || false) ? (data.contentSpacingMobile || 20) : (data.contentSpacing || 40);
                                if (isMobileLayout || false) {
                                    return `0 ${contentSpacing}px`;
                                }
                                if (layoutConfig.direction === 'column') {
                                    return `0 ${contentSpacing}px`;
                                }
                                // ✅ SIDE-BY-SIDE LAYOUTS: Einstellbarer Margin zum Rand (nur Desktop)
                                const imageMargin = data.imageMarginToEdge || 40;
                                if (layoutConfig.direction === 'row') {
                                    return `0 0 0 ${imageMargin}px`; // Links: Margin, Rechts: 0
                                } else if (layoutConfig.direction === 'row-reverse') {
                                    return `0 ${imageMargin}px 0 0`; // Rechts: Margin, Links: 0
                                }
                                return '0'; // Fallback
                            })()
                        })
                    }}>
                    <div style={{
                        position: 'relative', 
                        // ✅ FULL-HEIGHT FIX: Flex statt inline-block für Full-Height Modi
                        display: (layoutConfig.imageMode === 'fullHeight' && !isMobileLayout) ? 'flex' : 'inline-block',
                        // ✅ FULL-HEIGHT: Container nimmt volle Höhe
                        height: (layoutConfig.imageMode === 'fullHeight' && !isMobileLayout) ? '100%' : 'auto',
                        width: (layoutConfig.imageMode === 'fullHeight' && !isMobileLayout) ? '100%' : 'auto',
                        alignItems: (layoutConfig.imageMode === 'fullHeight' && !isMobileLayout) ? 'stretch' : 'center',
                        justifyContent: (layoutConfig.imageMode === 'fullHeight' && !isMobileLayout) ? 'center' : 'center'
                    }}
                        onMouseEnter={(e) => {
                            const uploadBtn = e.currentTarget.querySelector('.profile-upload-btn');
                            if (uploadBtn) uploadBtn.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                            const uploadBtn = e.currentTarget.querySelector('.profile-upload-btn');
                            if (uploadBtn) uploadBtn.style.opacity = '0';
                        }}
                    >
                        <img 
                            src={data.profileImage || defaultProfileImage}
                            alt="Profile"
                            className={layoutConfig.imageMode === 'fullHeight' || layoutConfig.imageMode === 'fullWidth' ? '' : getImageShapeClass()}
                            style={{
                                ...getImageStyles(),
                                // ✅ IMAGE OPACITY CONTROL
                                opacity: (data.imageOpacity || 100) / 100
                            }}
                        />
                        {/* Profile Upload Button */}
                        {isEditMode && (
                            <button
                                onClick={() => handleImageUpload('profileImage', setIsUploadingProfile)}
                                style={{
                                    position: 'absolute',
                                    top: '-10px',
                                    left: '-10px',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    background: 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'all 0.2s ease',
                                    zIndex: 100,
                                    boxShadow: '0 1px 3px rgba(99, 102, 241, 0.3)'
                                }}
                                title="Upload Profile Image"
                                className="profile-upload-btn"
                            >
                                {isUploadingProfile ? (
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid white',
                                        borderTop: '2px solid transparent',
                                        borderRadius: '50%',
                                        animation: 'heroImageUploadSpin 1s linear infinite'
                                    }} />
                                ) : (
                                    <img 
                                        src="/img/upload.svg" 
                                        alt="Upload" 
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            filter: 'invert(1)'
                                        }}
                                    />
                                )}
                            </button>
                        )}
                    </div>
                    </div>
                )}

                {/* Text Content - Simplified für Full-Height */}
                <div style={{
                    // ✅ SIMPLIFIED FULL-HEIGHT TEXT CONTAINER
                    ...(layoutConfig.imageMode === 'fullHeight' && !isMobileLayout ? {
                        // FULL-HEIGHT: Einfacher Text-Container
                        flex: '1',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: layoutConfig.textAlign === 'center' ? 'center' : (layoutConfig.textAlign === 'right' ? 'flex-end' : 'flex-start'),
                        height: '100%',
                        padding: `0 ${(data.contentSpacing || 40)}px`, // Nur horizontales Padding
                        gap: getResponsiveValue(data, 'elementSpacing', false, 20) + 'px',
                        textAlign: layoutConfig.textAlign,
                        lineHeight: data.textLineHeight || 1.6
                    } : {
                        // STANDARD LAYOUTS: Normale Text-Container Logic
                        flex: layoutConfig.textFlex,
                        textAlign: isMobileLayout ? 'center' : layoutConfig.textAlign,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: layoutConfig.direction === 'column' ? 'center' : 'flex-start',
                        alignItems: isMobileLayout ? 'center' : (layoutConfig.textAlign === 'center' ? 'center' : (layoutConfig.textAlign === 'right' ? 'flex-end' : 'flex-start')),
                        width: '100%',
                        gap: getResponsiveValue(data, 'elementSpacing', isMobileLayout, isMobileLayout ? 15 : 20) + 'px',
                        lineHeight: data.textLineHeight || 1.6,
                        padding: (() => {
                            const contentSpacing = (isMobileLayout || false) ? (data.contentSpacingMobile || 20) : (data.contentSpacing || 40);
                            if (isMobileLayout || false) {
                                return `0 ${contentSpacing}px`;
                            }
                            if (layoutConfig.direction === 'row') {
                                return `0 ${contentSpacing}px 0 0`;
                            } else if (layoutConfig.direction === 'row-reverse') {
                                return `0 0 0 ${contentSpacing}px`;
                            } else {
                                return `0 ${contentSpacing}px`;
                            }
                        })()
                    })
                }}>
                    {/* H2 - Primary Heading (Primary Font) - EDITIERBAR */}
                    {data.showGreetingText !== false && renderEditableText(
                        'greetingText',
                        data.greetingText,
                        'h2',
                        {
                            ...((!data.greetingEffect || data.greetingEffect === 'none') && {
                                color: data.greetingColor || data.titleColor || '#333333'
                            }),
                            fontSize: appendPx(data.greetingSize || 48),
                            fontWeight: data.greetingWeight || '700',
                            fontFamily: data.greetingFont || fonts.globalBigFont,
                            marginBottom: '16px',
                            lineHeight: '1.2',
                            letterSpacing: '-0.02em',
                            ...getTextEffectStyles(data.greetingEffect),
                            '--mobile-size': appendPx(data.greetingSizeMobile || data.greetingSize || 48),
                            '--normal-size': appendPx(data.greetingSize || 48),
                            '--big-size': appendPx(data.greetingSizeBig || data.greetingSize || 48),
                            '--mobile-weight': data.greetingWeightMobile || data.greetingWeight || '700',
                            '--normal-weight': data.greetingWeight || '700',
                            '--big-weight': data.greetingWeightBig || data.greetingWeight || '700',
                            // ✅ TEXT WIDTH für H2-Element - Direkte Width-Kontrolle
                            width: getResponsiveValue(data, 'textMaxWidth', isMobileLayout || false, (isMobileLayout || false) ? 400 : 600) + 'px',
                            minWidth: '0',
                            maxWidth: 'none'
                        },
                        "Hi, I'm..."
                    )}

                    {/* H4 - Secondary Heading mit HighlightText Support */}
                    {data.showTitleText !== false && (
                        <div>
                            {/* ✅ CLEAN CONDITION: Inline-Editing nur wenn KEINE Highlights aktiv */}
                            {(!data.titleHighlightWords || data.titleHighlightWords.trim() === '') ? (
                                // 🔧 NORMALE INLINE-EDITING (ohne Highlights)
                                renderEditableText(
                                    'titleText',
                                    data.titleText,
                                    'h4',
                                    {
                                        ...((!data.titleEffect || data.titleEffect === 'none') && {
                                            color: data.titleColor || '#333333'
                                        }),
                                    fontSize: appendPx(data.titleSize || 24),
                                    fontWeight: data.titleWeight || '600',
                                    fontFamily: data.titleFont || fonts.globalSmallFont,
                                    marginBottom: '20px',
                                    lineHeight: '1.4',
                                    ...getTextEffectStyles(data.titleEffect),
                                    '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 24),
                                    '--normal-size': appendPx(data.titleSize || 24),
                                    '--big-size': appendPx(data.titleSizeBig || data.titleSize || 24),
                                    '--mobile-weight': data.titleWeightMobile || data.titleWeight || '600',
                                    '--normal-weight': data.titleWeight || '600',
                                    '--big-weight': data.titleWeightBig || data.titleWeight || '600',
                                    // ✅ TEXT WIDTH für H4-Element - Direkte Width-Kontrolle (konsistent mit anderen)
                                    width: getResponsiveValue(data, 'textMaxWidth', isMobileLayout || false, (isMobileLayout || false) ? 400 : 600) + 'px',
                                    minWidth: '0',
                                    maxWidth: 'none'
                                    },
                                    'Enter your title...'
                                )
                            ) : (
                                // 🎨 HIGHLIGHT-MODUS (nur Anzeige, kein Inline-Editing)
                                <h4 style={{
                                    ...((!data.titleEffect || data.titleEffect === 'none') && {
                                        color: data.titleColor || '#333333'
                                    }),
                                    fontSize: appendPx(data.titleSize || 24),
                                    fontWeight: data.titleWeight || '600',
                                    fontFamily: data.titleFont || fonts.globalSmallFont,
                                    marginBottom: '20px',
                                    lineHeight: '1.4',
                                    whiteSpace: 'pre-wrap',
                                    ...getTextEffectStyles(data.titleEffect),
                                    '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 24),
                                    '--normal-size': appendPx(data.titleSize || 24),
                                    '--big-size': appendPx(data.titleSizeBig || data.titleSize || 24),
                                    '--mobile-weight': data.titleWeightMobile || data.titleWeight || '600',
                                    '--normal-weight': data.titleWeight || '600',
                                    '--big-weight': data.titleWeightBig || data.titleWeight || '600',
                                    // ✅ TEXT WIDTH für H4-Element (Highlight-Modus) - Direkte Width-Kontrolle
                                    width: getResponsiveValue(data, 'textMaxWidth', isMobileLayout || false, (isMobileLayout || false) ? 400 : 600) + 'px',
                                    minWidth: '0',
                                    maxWidth: 'none'
                                }}>
                                    <HighlightText
                                        text={data.titleText}
                                        highlightWords={data.titleHighlightWords}
                                        baseStyle={{}}
                                        highlightFont={data.titleHighlightFont || fonts.globalBigFont}
                                        highlightColor={data.titleHighlightColor}
                                        highlightWeight={data.titleHighlightWeight}
                                        highlightSize={data.titleHighlightSize || data.titleSize}
                                        highlightSizeMobile={data.titleHighlightSizeMobile || data.titleSizeMobile}
                                        highlightSizeBig={data.titleHighlightSizeBig || data.titleSizeBig}
                                        highlightEffect={data.titleHighlightEffect}
                                        highlightMarginLeft={data.titleHighlightMarginLeft}
                                        highlightMarginRight={data.titleHighlightMarginRight}
                                        getTextEffectStyles={getTextEffectStyles}
                                    />
                                </h4>
                            )}
                        </div>
                    )}

                    {/* P - Description Text (Secondary Font) - EDITIERBAR */}
                    {data.showDescriptionText !== false && renderEditableText(
                        'descriptionText',
                        data.descriptionText,
                        'p',
                        {
                            ...((!data.descriptionEffect || data.descriptionEffect === 'none') && {
                                color: data.descriptionColor || '#666666'
                            }),
                            fontSize: appendPx(data.descriptionSize || 18),
                            fontWeight: data.descriptionWeight || '400',
                            fontFamily: data.descriptionFont || fonts.globalSmallFont,
                            marginBottom: '32px',
                            lineHeight: '1.6',
                            ...getTextEffectStyles(data.descriptionEffect),
                            '--mobile-size': appendPx(data.descriptionSizeMobile || data.descriptionSize || 18),
                            '--normal-size': appendPx(data.descriptionSize || 18),
                            '--big-size': appendPx(data.descriptionSizeBig || data.descriptionSize || 18),
                            '--mobile-weight': data.descriptionWeightMobile || data.descriptionWeight || '400',
                            '--normal-weight': data.descriptionWeight || '400',
                            '--big-weight': data.descriptionWeightBig || data.descriptionWeight || '400',
                            // ✅ TEXT WIDTH für P-Element - Direkte Width-Kontrolle
                            width: getResponsiveValue(data, 'textMaxWidth', isMobileLayout || false, (isMobileLayout || false) ? 400 : 600) + 'px',
                            minWidth: '0',
                            maxWidth: 'none'
                        },
                        'Enter description...'
                    )}

                    {/* CTA Button - Revolutionary Button Style System */}
                    {data.showButtonText !== false && (
                        <div style={{
                            // ✅ BUTTON WRAPPER für garantierte maxWidth-Funktion
                            width: '100%',
                            display: 'flex',
                            justifyContent: isMobileLayout ? 'center' : 
                                          (layoutConfig.textAlign === 'center' ? 'center' : 
                                           (layoutConfig.textAlign === 'right' ? 'flex-end' : 'flex-start')),
                            // ✅ KRITISCH: Wrapper darf maxWidth nicht überschreiben
                            alignItems: 'center'
                        }}>
                            {renderEditableText(
                                'buttonText',
                                data.buttonText,
                                'button',
                                {
                            // 🎨 GRADIENT vs. SOLID COLOR
                            background: data.buttonGradientEnabled 
                                ? `linear-gradient(135deg, ${data.buttonGradientColor1 || '#667eea'} 0%, ${data.buttonGradientColor2 || '#764ba2'} 100%)`
                                : (data.buttonColor || '#5C80BC'),
                                
                            // 🎬 ANIMATIONS
                            backgroundSize: data.buttonGradientEnabled && data.buttonGradientAnimation ? '300% 300%' : 'auto',
                            animation: data.buttonGradientEnabled && data.buttonGradientAnimation 
                                ? 'gradientShift 4s ease-in-out infinite' 
                                : data.buttonAnimationsEnabled && data.buttonAnimationType && data.buttonAnimationType !== 'none'
                                ? `${data.buttonAnimationType} ${data.buttonAnimationSpeed || 2000}ms ease-in-out infinite`
                                : 'none',
                                
                            // 🎨 COLORS & STYLING
                            color: data.buttonTextColor || '#ffffff',
                            
                            // 🔲 BORDER
                            border: data.buttonBorderEnabled && data.buttonBorderWidth && data.buttonBorderWidth > 0
                                ? `${data.buttonBorderWidth}px solid ${data.buttonBorderColor || '#5C80BC'}`
                                : 'none',
                                
                            // 🌟 SHADOW
                            boxShadow: data.buttonShadowEnabled 
                                ? `0 ${data.buttonShadowDistance || 8}px ${data.buttonShadowIntensity || 15}px ${data.buttonShadowColor || '#000000'}${Math.round((data.buttonShadowOpacity || 25) * 2.55).toString(16).padStart(2, '0')}`
                                : 'none',
                                
                            // 📐 RESPONSIVE DIMENSIONS
                            fontSize: isMobileLayout 
                                ? appendPx(data.buttonTextSizeMobile || data.buttonTextSize || 18) 
                                : appendPx(data.buttonTextSize || 18),
                            fontWeight: isMobileLayout 
                                ? (data.buttonTextWeightMobile || data.buttonTextWeight || '600') 
                                : (data.buttonTextWeight || '600'),
                            fontFamily: data.buttonFont || fonts.globalSmallFont,
                            // ✅ BUTTON WIDTH SYSTEM: Hybrid-Strategie (% width + maxWidth constraint)
                            width: isMobileLayout 
                                ? appendPercent(data.buttonWidthMobile || data.buttonWidth || 100) 
                                : appendPercent(data.buttonWidth || 100),
                            maxWidth: isMobileLayout 
                                ? appendPx(data.buttonMaxWidthMobile || data.buttonMaxWidth || 300) 
                                : appendPx(data.buttonMaxWidth || 300),
                            minWidth: '120px', // ✅ Minimum width to prevent too small buttons
                            height: isMobileLayout 
                                ? appendPx(data.buttonHeightMobile || data.buttonHeight || 50) 
                                : appendPx(data.buttonHeight || 50),
                            borderRadius: isMobileLayout 
                                ? appendPx(data.buttonBorderRadiusMobile || data.buttonBorderRadius || 8) 
                                : appendPx(data.buttonBorderRadius || 8),
                                
                            // 🎯 LAYOUT PROPERTIES
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            textDecoration: 'none',
                            outline: 'none',
                            // ✅ BUTTON ALIGNMENT: Keine alignSelf-Überschreibung für maxWidth
                                },
                                'Enter button text...'
                            )}
                        </div>
                    )}
                </div>
            </div>
            </div>
        </>
    );
}

export default HiWidget;
